import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { applyThemeToDocument, resolveSystemTheme, resolveThemeId } from "./applyTheme";
import { THEME_STORAGE_KEY, themeModes, themes } from "./themes";
import { useCanonicalFonts } from "./useCanonicalFonts";

const ThemeContext = createContext(null);

function readStoredMode() {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    if (themeModes.includes(v)) return v;
  } catch {
    /* storage */
  }
  return null;
}

export function ThemeProvider({ children, defaultMode = "light" }) {
  useCanonicalFonts();

  const [mode, setModeState] = useState(() => {
    const stored = readStoredMode();
    return stored ?? defaultMode;
  });
  const [resolvedThemeId, setResolvedThemeId] = useState(() => resolveThemeId(mode));

  const apply = useCallback((nextMode) => {
    const id = resolveThemeId(nextMode);
    setResolvedThemeId(id);
    applyThemeToDocument(id);
  }, []);

  useEffect(() => {
    apply(mode);
  }, [mode, apply]);

  useEffect(() => {
    if (mode !== "system") return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => apply("system");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [mode, apply]);

  const setMode = useCallback((next) => {
    if (!themeModes.includes(next)) return;
    setModeState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* storage */
    }
  }, []);

  const value = useMemo(
    () => ({
      mode,
      resolvedThemeId,
      theme: themes[resolvedThemeId] || themes.light,
      setMode,
      themes,
      themeModes,
    }),
    [mode, resolvedThemeId, setMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
}

/** Non-throwing hook for optional theme access in tests */
export function useThemeOptional() {
  return useContext(ThemeContext);
}
